import React, { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Input from "../common/Input";
import Button from "../common/Button";
import {
  UserCreateFormData,
  userCreateSchema,
  UserUpdateFormData,
  userUpdateSchema,
} from "../../schemas/userSchema";
import Dropdown from "../common/Dropdown";
import { useRoleOptions } from "../../hooks/useRoleOptions";

interface UserFormProps {
  initialData?: Partial<UserUpdateFormData>;
  onSubmit: (data: UserCreateFormData | UserUpdateFormData) => void;
  onCancel: () => void;
  isLoading?: boolean;
  isUpdate?: boolean;
  isOpen?: boolean;
}

const UserForm: React.FC<UserFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  isLoading = false,
  isUpdate = false,
  isOpen = true,
}) => {
  const schema = isUpdate ? userUpdateSchema : userCreateSchema;
  const form = useForm<UserCreateFormData | UserUpdateFormData>({
    // @ts-ignore - Type mismatch between create and update schemas is expected
    resolver: zodResolver(schema),
    mode: "onBlur",
    reValidateMode: "onBlur",
    defaultValues: {
      name: initialData?.name || "",
      username: initialData?.username || "",
      email: initialData?.email || "",
      roleId: initialData?.roleId || "",
      phoneNumber: initialData?.phoneNumber || "",
    },
  });

  const {
    handleSubmit,
    setValue,
    formState: { errors },
    watch,
    reset,
    trigger,
  } = form;

  // Track if modal was just opened to prevent resetting during submission
  const prevIsOpenRef = useRef(false);

  // Reset form only when modal opens (not on every render or during submission)
  useEffect(() => {
    // Only reset when modal transitions from closed to open
    if (isOpen && !prevIsOpenRef.current) {
      if (initialData) {
        reset({
          name: initialData.name || "",
          username: initialData.username || "",
          email: initialData.email || "",
          roleId: initialData.roleId || "",
          phoneNumber: initialData.phoneNumber || "",
        });
      } else {
        reset({
          name: "",
          username: "",
          email: "",
          roleId: "",
          phoneNumber: "",
        });
      }
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen, initialData, reset]);

  const { roleOptions, isRoleOptionsLoading } = useRoleOptions();

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <Input
          label="Name"
          placeholder="Enter full name"
          name="name"
          value={watch("name") || ""}
          onChange={(e) => setValue("name", e.target.value, { shouldValidate: false })}
          onBlur={() => trigger("name")}
          error={errors.name}
        />
        <Input
          label="Username"
          placeholder="Enter username"
          name="username"
          value={watch("username") || ""}
          onChange={(e) => setValue("username", e.target.value, { shouldValidate: false })}
          onBlur={() => trigger("username")}
          error={errors.username}
        />
        <Input
          label="Email"
          placeholder="Enter email"
          name="email"
          value={watch("email") || ""}
          onChange={(e) => setValue("email", e.target.value, { shouldValidate: false })}
          onBlur={() => trigger("email")}
          error={errors.email}
        />
        <Dropdown
          label="Select Role"
          options={roleOptions || []}
          value={watch("roleId")}
          onChange={(val) => setValue("roleId", val as string, { shouldValidate: false })}
          onBlur={() => trigger("roleId")}
          error={errors.roleId}
          disabled={isRoleOptionsLoading}
          name="roleId"
        />
        <Input
          label="Phone Number"
          placeholder="Enter phone number"
          name="phoneNumber"
          value={watch("phoneNumber") || ""}
          onChange={(e) => setValue("phoneNumber", e.target.value, { shouldValidate: false })}
          onBlur={() => trigger("phoneNumber")}
          error={errors.phoneNumber}
          allowedChars="numeric"
        />
      </div>
      <div className="flex justify-end space-x-4">
        <Button
          type="button"
          variant="secondary"
          onClick={onCancel}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          isLoading={isLoading}
          className="w-full sm:w-auto"
        >
          {isUpdate ? "Update" : "Create"}
        </Button>
      </div>
    </form>
  );
};

export default UserForm;
